import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import api from '../../lib/api';
import type {
  User,
  LoginCredentials,
  RegisterData,
  TokenResponse,
  EmailVerificationRequest,
  ForgotPasswordRequest,
  PasswordResetRequest,
  ChangePasswordRequest,
  UserUpdate,
  UserInvite,
  MessageResponse,
} from '../../types/index';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const getUserFromStorage = (): User | null => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (e) {
    return null;
  }
};

const initialState: AuthState = {
  user: getUserFromStorage(),
  accessToken: localStorage.getItem('access_token'),
  refreshToken: localStorage.getItem('refresh_token'),
  isAuthenticated: !!localStorage.getItem('access_token'),
  loading: false,
  error: null,
};

// Async thunks
export const register = createAsyncThunk(
  'auth/register',
  async (data: RegisterData, { rejectWithValue }) => {
    try {
      const response = await api.post<MessageResponse>('/auth/register', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.detail || 'Registration failed');
    }
  }
);

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async (data: EmailVerificationRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<MessageResponse>('/auth/verify-email', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.detail || 'Verification failed');
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await api.post<any>('/auth/login', credentials);
      const tokenData = response.data.data || response.data;
      localStorage.setItem('access_token', tokenData.access_token);
      localStorage.setItem('refresh_token', tokenData.refresh_token);
      localStorage.setItem('user', JSON.stringify(tokenData.user));
      return tokenData;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.detail || 'Login failed');
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (data: ForgotPasswordRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<MessageResponse>('/auth/forgot-password', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.detail || 'Request failed');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (data: PasswordResetRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<MessageResponse>('/auth/reset-password', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.detail || 'Reset failed');
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (data: ChangePasswordRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<MessageResponse>('/auth/change-password', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.detail || 'Password change failed');
    }
  }
);

export const getProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<any>('/auth/profile');
      const userData = response.data.data || response.data;
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.detail || 'Failed to fetch profile');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (data: UserUpdate, { rejectWithValue }) => {
    try {
      const response = await api.put<any>('/auth/profile', data);
      const userData = response.data.data || response.data;
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.detail || 'Update failed');
    }
  }
);

export const inviteUser = createAsyncThunk(
  'auth/inviteUser',
  async (data: UserInvite, { rejectWithValue }) => {
    try {
      const response = await api.post<MessageResponse>('/auth/invite', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.detail || 'Invitation failed');
    }
  }
);

export const resendVerification = createAsyncThunk(
  'auth/resendVerification',
  async (data: ForgotPasswordRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<MessageResponse>('/auth/resend-verification', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.detail || 'Request failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(login.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action: PayloadAction<TokenResponse>) => {
      state.loading = false;
      state.user = action.payload.user;
      state.accessToken = action.payload.access_token;
      state.refreshToken = action.payload.refresh_token;
      state.isAuthenticated = true;
    });
    builder.addCase(login.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Get Profile
    builder.addCase(getProfile.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getProfile.fulfilled, (state, action: PayloadAction<User>) => {
      state.loading = false;
      state.user = action.payload;
    });
    builder.addCase(getProfile.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update Profile
    builder.addCase(updateProfile.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateProfile.fulfilled, (state, action: PayloadAction<User>) => {
      state.loading = false;
      state.user = action.payload;
    });
    builder.addCase(updateProfile.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Register
    builder.addCase(register.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(register.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(register.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Other async actions loading states
    builder.addMatcher(
      (action) => action.type.endsWith('/pending'),
      (state) => {
        state.loading = true;
        state.error = null;
      }
    );
    builder.addMatcher(
      (action) => action.type.endsWith('/fulfilled') && !action.type.includes('login') && !action.type.includes('Profile'),
      (state) => {
        state.loading = false;
      }
    );
    builder.addMatcher(
      (action) => action.type.endsWith('/rejected') && !action.type.includes('login') && !action.type.includes('Profile'),
      (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.error = action.payload;
      }
    );
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
